// =============================================================================
// pages/R2VendingMachineLanding.tsx
// R2 Marketplace — Public AI Engineering Services Catalog
// No login required. "What can R2 do for me?" answers immediately.
// Design: NASA mission control meets Autodesk construction technology
// =============================================================================
import { useState, useCallback, useMemo, useRef, useEffect, type JSX } from 'react';
import {
  Search, ChevronRight, ChevronLeft, ChevronDown, Clock, Shield, Zap, Star, Package,
  FileText, Building2, Server, Heart, Globe, Lock, Wifi,
  Layers, GitBranch, Cpu, Radio, BarChart3, Video,
  X, CheckCircle2, Play, LogIn, ShoppingCart, Loader2,
  MonitorDot, FlaskConical, Wrench, Bot, Send, Eye, Sparkles, SlidersHorizontal,
} from 'lucide-react';
import {
  R2PreviewModal,
  type ServiceAnimationType,
} from '../components/R2PreviewAnimationEngine.js';
import { R2ServiceDiscoveryModal } from '../components/R2ServiceDiscoveryModal.js';
import { LearnThisFeature } from '../components/academy/LearnThisFeature.js';
import { MiniSheetPreview, PreviewExperiencePanel } from '../components/PreviewExperiencePanel.js';
import { R2ShowMeWalkthrough } from '../components/R2ShowMeWalkthrough.js';
import { R2FounderBetaProgram } from '../components/R2FounderBetaProgram.js';
import { evaluateCommercialAccess, getProductForService, R2_PRODUCT_CATALOG, setCommercialMode, type R2CommercialMode } from '../lib/commercialControl.js';
import { getFounderToken, hasFounderToken, useFounderMode } from '../stores/founderMode.store.js';
import { useAuthStore } from '../stores/auth.store.js';
import { isMasterAdmin } from '@bim/shared/config/masterKey.config.js';

const API_URL = (import.meta as any).env?.VITE_BACKEND_URL
  ?? (typeof process !== 'undefined' ? (process.env as any)?.REACT_APP_BACKEND_URL ?? '' : '');

// ─── Analytics helper ─────────────────────────────────────────────────────────
async function trackFunnel(event: string, serviceId: string, meta?: Record<string, unknown>) {
  try {
    await fetch(`${API_URL}/api/r2-marketplace/analytics/funnel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, service_id: serviceId, meta: meta ?? {}, ts: new Date().toISOString() }),
    });
  } catch { /* non-blocking */ }
}

function recordFounderCheckoutAudit(entry: Record<string, unknown>): string {
  const auditId = `FOUNDER-${Date.now().toString(36).toUpperCase()}`;
  try {
    const key = 'r2_founder_checkout_audit';
    const current = JSON.parse(localStorage.getItem(key) || '[]');
    current.unshift({ audit_id: auditId, created_at: new Date().toISOString(), ...entry });
    localStorage.setItem(key, JSON.stringify(current.slice(0, 50)));
  } catch { /* local founder audit is best-effort for offline mode */ }
  return auditId;
}

// ─── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:       '#010812',
  panel:    'rgba(4,12,28,0.94)',
  card:     'rgba(6,15,35,0.92)',
  border:   'rgba(0,212,255,0.15)',
  borderHov:'rgba(0,212,255,0.45)',
  cyan:     '#00d4ff',
  orange:   '#ff6b35',
  green:    '#4ade80',
  amber:    '#fbbf24',
  text:     '#f0f6ff',
  muted:    'rgba(240,246,255,0.55)',
  dim:      'rgba(0,212,255,0.07)',
  mono:     "'JetBrains Mono','Space Mono','Courier New',monospace",
};

// ─── Service catalog ───────────────────────────────────────────────────────────
interface Service {
  id: string;
  name: string;
  category: string;
  description: string;
  basePrice: number;
  deliveryHours: number;
  tier: 'starter' | 'professional' | 'enterprise';
  animationType: ServiceAnimationType;
  featured?: boolean;
  badge?: string;
}

function serviceCommercial(service: Service) {
  return evaluateCommercialAccess(getProductForService(service.id).id);
}

const PRIMARY_CATS = ['All Services', 'Floor Plans', 'BIM & Digital Twin', 'Healthcare', 'Data Centre', 'OSP & Telecom', 'Security', 'Infrastructure'];
const MORE_CATS    = ['Electrical', 'Mechanical', 'Fire Alarm', 'Audio Visual', 'ICT', 'Nurse Call', 'Wireless', 'Outside Plant', 'Civil', 'Architecture', 'Energy', 'Industrial', 'Mining', 'Utilities', 'Rail', 'Airport', 'Smart Buildings', 'Residential', 'Commercial', 'Government'];
const ALL_CATS     = [...PRIMARY_CATS, ...MORE_CATS];
const CATEGORIES   = ALL_CATS; // kept for any downstream reference

const SERVICES: Service[] = [
  // ── Floor Plans & Documentation ────────────────────────────────────────────
  { id: 'floor-plan-gen',   name: 'Floor Plan Generator',        category: 'Floor Plans',      description: 'Upload PDF/CAD — R2 extracts intelligent floor plan with room classifications, area calculations, and system overlays.',   basePrice: 299,   deliveryHours: 4,  tier: 'starter',      animationType: 'floor_plan',     featured: true,  badge: 'Most Popular' },
  { id: 'asbuilt-conv',     name: 'As-Built Conversion',          category: 'Floor Plans',      description: 'Convert legacy drawings, photos, or scanned documents into editable, intelligent as-built BIM documentation.',              basePrice: 499,   deliveryHours: 12, tier: 'professional', animationType: 'floor_plan' },
  { id: 'permit-pkg',       name: 'Permit Package Generator',     category: 'Floor Plans',      description: 'AI-compiled permit-ready documentation package including drawings, schedules, code compliance notes, and submission checklist.',basePrice: 799,   deliveryHours: 24, tier: 'professional', animationType: 'floor_plan',     badge: 'Code Ready' },
  { id: 'shop-dwg',         name: 'Shop Drawing Creation',        category: 'Floor Plans',      description: 'Fabrication-ready shop drawings from design documents. Includes tolerances, material specs, and installation sequences.',    basePrice: 599,   deliveryHours: 18, tier: 'professional', animationType: 'floor_plan' },
  { id: 'const-docs',       name: 'Construction Documentation',   category: 'Floor Plans',      description: 'Complete construction document set including IFC/RFI-ready sheets, specifications, and submittal packages.',                 basePrice: 1499,  deliveryHours: 48, tier: 'enterprise',   animationType: 'floor_plan' },
  { id: 'site-survey',      name: 'Site Survey Package',          category: 'Floor Plans',      description: 'Structured site survey forms, field documentation templates, and post-survey drawing coordination package.',                 basePrice: 349,   deliveryHours: 8,  tier: 'starter',      animationType: 'generic' },

  // ── BIM & Digital Twin ─────────────────────────────────────────────────────
  { id: 'digital-twin',     name: 'Digital Twin Creation',        category: 'BIM & Digital Twin', description: '2D drawings transformed into a fully intelligent, data-rich 3D digital twin with live data layers and asset tracking.',  basePrice: 2499,  deliveryHours: 72, tier: 'enterprise',   animationType: 'digital_twin',   featured: true, badge: 'Signature Service' },
  { id: 'bim-model',        name: 'BIM Model Generation',         category: 'BIM & Digital Twin', description: 'LOD 300–400 BIM model created from sketches, PDFs, or CAD files. IFC-compliant, Revit-native.',                         basePrice: 1999,  deliveryHours: 48, tier: 'enterprise',   animationType: 'digital_twin' },
  { id: 'revit-services',   name: 'Revit Services',               category: 'BIM & Digital Twin', description: 'Expert Revit modelling, family creation, template setup, and BIM execution plan documentation.',                         basePrice: 899,   deliveryHours: 24, tier: 'professional', animationType: 'digital_twin' },
  { id: 'autocad-draft',    name: 'AutoCAD Drafting',             category: 'BIM & Digital Twin', description: 'Professional AutoCAD drafting from red-lines, sketches, or verbal scope. Any discipline.',                               basePrice: 399,   deliveryHours: 12, tier: 'starter',      animationType: 'floor_plan' },
  { id: 'ai-drawing-review',name: 'AI Drawing Review',            category: 'BIM & Digital Twin', description: 'Automated drawing review against standards and specifications. Returns markup PDF with issues categorised by severity.',  basePrice: 299,   deliveryHours: 2,  tier: 'starter',      animationType: 'generic',        badge: 'AI-Powered' },
  { id: 'qaqc-review',      name: 'QA/QC Review Package',         category: 'BIM & Digital Twin', description: 'Comprehensive quality assurance review of project documents. Includes clash detection, standards compliance, and sign-off.',basePrice: 699,   deliveryHours: 16, tier: 'professional', animationType: 'generic' },

  // ── Healthcare ─────────────────────────────────────────────────────────────
  { id: 'hospital-design',  name: 'Hospital Telecom Design',      category: 'Healthcare',       description: 'Complete hospital ICT package: nurse call, PACS, structured cabling, wireless, RTLS, and clinical systems coordination.',   basePrice: 3999,  deliveryHours: 96, tier: 'enterprise',   animationType: 'healthcare',     featured: true, badge: 'Clinical Grade' },
  { id: 'healthcare-bim',   name: 'Healthcare BIM',               category: 'Healthcare',       description: 'Healthcare-specific BIM modelling with clinical zone classifications, infection control overlays, and equipment integration.',basePrice: 2999,  deliveryHours: 72, tier: 'enterprise',   animationType: 'healthcare' },
  { id: 'nurse-call',       name: 'Nurse Call Design',            category: 'Healthcare',       description: 'Full nurse call system design including IP backbone, pendant locations, code blue, staff assist, and RTLS integration.',    basePrice: 1299,  deliveryHours: 24, tier: 'professional', animationType: 'healthcare',     badge: 'ANSI/TIA Compliant' },
  { id: 'equip-schedule',   name: 'Equipment Scheduling',         category: 'Healthcare',       description: 'Comprehensive medical equipment schedules including power, data, gas, and telecom requirements per room type.',             basePrice: 899,   deliveryHours: 18, tier: 'professional', animationType: 'healthcare' },

  // ── Data Centre ────────────────────────────────────────────────────────────
  { id: 'dc-design',        name: 'Data Centre Design',           category: 'Data Centre',      description: 'Full Tier I–IV data centre design: rack layout, power (kW/rack), cooling, fiber infrastructure, and DCIM documentation.',  basePrice: 4999,  deliveryHours: 96, tier: 'enterprise',   animationType: 'data_center',    badge: 'Tier Certified' },
  { id: 'rack-elevation',   name: 'Rack Elevation Generator',     category: 'Data Centre',      description: 'U-space optimised rack elevations with equipment placement, cable management, power load sheets, and labelling guides.',     basePrice: 599,   deliveryHours: 8,  tier: 'starter',      animationType: 'rack_elevation' },
  { id: 'telecom-room',     name: 'Telecom Room Layout',          category: 'Data Centre',      description: 'MDF/IDF room layout with rack positions, cable pathway, A/C placement, fire suppression, and access control design.',      basePrice: 799,   deliveryHours: 12, tier: 'professional', animationType: 'data_center' },

  // ── OSP & Telecom ──────────────────────────────────────────────────────────
  { id: 'osp-design',       name: 'OSP Design Package',           category: 'OSP & Telecom',    description: 'Outside plant fiber design: aerial and underground routes, manholes, handholes, splice points, and BOQ.',                   basePrice: 2499,  deliveryHours: 48, tier: 'enterprise',   animationType: 'gis_osp',        badge: 'GIS-Enabled' },
  { id: 'fiber-backbone',   name: 'Fiber Backbone Design',        category: 'OSP & Telecom',    description: 'Campus or building fiber backbone design: structured cabling plan, pathway layout, splice schedule, and OTDR test requirements.',basePrice: 1299, deliveryHours: 24, tier: 'professional', animationType: 'gis_osp' },
  { id: 'riser-diagram',    name: 'Riser Diagram Generator',      category: 'OSP & Telecom',    description: 'Multi-floor riser diagrams showing vertical cable pathways, IDF/MDF details, and building backbone architecture.',          basePrice: 699,   deliveryHours: 8,  tier: 'professional', animationType: 'riser_diagram',  badge: 'BICSI Standard' },
  { id: 'das-design',       name: 'DAS Design',                   category: 'OSP & Telecom',    description: 'Distributed Antenna System design for cellular and public safety coverage. Includes RF model, BDA specs, and permit package.',basePrice: 2999,  deliveryHours: 48, tier: 'enterprise',   animationType: 'wifi_design' },
  { id: 'wifi-design',      name: 'Wi-Fi Network Design',         category: 'OSP & Telecom',    description: 'RF propagation modelling, AP placement optimisation, channel plan, and controller configuration for enterprise Wi-Fi.',      basePrice: 1499,  deliveryHours: 24, tier: 'professional', animationType: 'wifi_design' },

  // ── Security ───────────────────────────────────────────────────────────────
  { id: 'security-design',  name: 'Security Systems Design',      category: 'Security',         description: 'Integrated security design: CCTV, access control, intrusion detection, and mass notification. Full documentation package.',  basePrice: 1999,  deliveryHours: 36, tier: 'enterprise',   animationType: 'security' },
  { id: 'cctv-design',      name: 'CCTV Design',                  category: 'Security',         description: 'CCTV system design with camera placement, coverage analysis, recording infrastructure, and NDAA-compliant equipment specs.',  basePrice: 899,   deliveryHours: 12, tier: 'professional', animationType: 'security' },
  { id: 'access-control',   name: 'Access Control Design',        category: 'Security',         description: 'Door-by-door access control design with credential hardware specs, controller locations, and monitoring station layout.',     basePrice: 799,   deliveryHours: 12, tier: 'professional', animationType: 'security' },

  // ── Infrastructure ─────────────────────────────────────────────────────────
  { id: 'sld',              name: 'Single-Line Diagram',          category: 'Infrastructure',   description: 'Electrical single-line diagram with panel schedules, load calculations, protection coordination, and arc flash notation.',   basePrice: 999,   deliveryHours: 16, tier: 'professional', animationType: 'single_line' },
  { id: 'campus-design',    name: 'Campus Infrastructure Design', category: 'Infrastructure',   description: 'Multi-building campus telecom and power infrastructure: backbone, manholes, OSP conduit, and site electrical distribution.', basePrice: 4999,  deliveryHours: 96, tier: 'enterprise',   animationType: 'gis_osp',        badge: 'Multi-Building' },

  // ── Electrical ─────────────────────────────────────────────────────────────
  { id: 'elec-dist-pkg',    name: 'Electrical Distribution Design', category: 'Electrical',     description: 'Complete electrical distribution design package: panel schedules, load calculations, circuit routing, and NEC/CEC-compliant documentation.',          basePrice: 1299, deliveryHours: 24, tier: 'professional', animationType: 'single_line',   badge: 'Demo' },
  { id: 'elec-load-analysis',name: 'NEC/CEC Load Analysis Review',  category: 'Electrical',     description: 'Comprehensive load analysis per NEC/CEC Article 220. Demand factor calculations, service sizing, and code compliance annotations.',                   basePrice: 599,  deliveryHours: 8,  tier: 'starter',      animationType: 'single_line',   badge: 'Demo' },
  { id: 'elec-one-line',    name: 'One-Line Diagram Generation',    category: 'Electrical',     description: 'AI-generated one-line diagrams from equipment schedules or field data. Protection coordination, feeder sizing, and arc flash notation included.',       basePrice: 899,  deliveryHours: 12, tier: 'professional', animationType: 'single_line',   badge: 'Demo' },
  { id: 'elec-panel-sched', name: 'Panel Schedule Automation',      category: 'Electrical',     description: 'Automated panel schedule generation from circuit lists. Load balancing, spare capacity analysis, and as-built reconciliation.',                        basePrice: 399,  deliveryHours: 4,  tier: 'starter',      animationType: 'generic',       badge: 'Demo' },

  // ── Mechanical ─────────────────────────────────────────────────────────────
  { id: 'mech-hvac-coord',  name: 'HVAC Coordination Package',      category: 'Mechanical',     description: 'Full mechanical HVAC coordination: equipment scheduling, duct routing, air balance calculations, and ASHRAE-compliant documentation.',                 basePrice: 1999, deliveryHours: 36, tier: 'enterprise',   animationType: 'generic',       badge: 'Demo' },
  { id: 'mech-equip-room',  name: 'Equipment Room Layout',          category: 'Mechanical',     description: 'Detailed mechanical equipment room layout with access clearances, vibration isolation, drain routing, and maintenance corridors.',                    basePrice: 699,  deliveryHours: 12, tier: 'professional', animationType: 'generic',       badge: 'Demo' },
  { id: 'mech-bim-coord',   name: 'Mechanical BIM Coordination',    category: 'Mechanical',     description: 'Revit-native mechanical BIM coordination: clash detection, spatial coordination with structural and electrical, and LOD 350 deliverables.',            basePrice: 1499, deliveryHours: 48, tier: 'enterprise',   animationType: 'digital_twin',  badge: 'Demo' },

  // ── Fire Alarm ─────────────────────────────────────────────────────────────
  { id: 'fa-device-layout', name: 'Fire Alarm Device Layout',       category: 'Fire Alarm',     description: 'Code-compliant fire alarm device layout per NFPA 72. Smoke detector spacing, pull station placement, and annunciator zoning.',                      basePrice: 899,  deliveryHours: 16, tier: 'professional', animationType: 'generic',       badge: 'Demo' },
  { id: 'fa-nac-layout',    name: 'Notification Appliance Layout',  category: 'Fire Alarm',     description: 'Notification appliance circuit design: strobe placement, audibility calculations, and AHJ-ready documentation per NFPA 72 Chapter 18.',              basePrice: 699,  deliveryHours: 12, tier: 'professional', animationType: 'generic',       badge: 'Demo' },
  { id: 'fa-riser',         name: 'Fire Alarm Riser Diagram',       category: 'Fire Alarm',     description: 'Multi-floor fire alarm riser diagram with panel locations, circuit IDs, device counts, and code-compliant wiring topology.',                          basePrice: 599,  deliveryHours: 8,  tier: 'starter',      animationType: 'riser_diagram', badge: 'Demo' },

  // ── ICT ────────────────────────────────────────────────────────────────────
  { id: 'ict-struct-cabling',name: 'Structured Cabling Design',     category: 'ICT',            description: 'ANSI/TIA-568 compliant structured cabling design: horizontal runs, TR layout, pathways, and BICSI-standard documentation package.',                  basePrice: 1199, deliveryHours: 18, tier: 'professional', animationType: 'riser_diagram', badge: 'Demo' },
  { id: 'ict-fiber-backbone',name: 'Fiber Backbone Design',         category: 'ICT',            description: 'Building fiber backbone design: singlemode/multimode routing, splice schedules, enclosure locations, and OTDR acceptance test requirements.',          basePrice: 999,  deliveryHours: 16, tier: 'professional', animationType: 'gis_osp',       badge: 'Demo' },
  { id: 'ict-telecom-room', name: 'Telecom Room Layout',            category: 'ICT',            description: 'TR/MDF layout with rack footprints, cable tray routing, power distribution, and passive/active equipment placement per BICSI TDMM.',                 basePrice: 799,  deliveryHours: 10, tier: 'professional', animationType: 'data_center',   badge: 'Demo' },
  { id: 'ict-rack-elevation',name: 'Rack Elevation Package',        category: 'ICT',            description: 'U-space rack elevations for ICT equipment: patch panels, switches, servers, cable management, and power budget per rack.',                            basePrice: 499,  deliveryHours: 6,  tier: 'starter',      animationType: 'rack_elevation',badge: 'Demo' },

  // ── Smart Buildings ────────────────────────────────────────────────────────
  { id: 'sb-iot-deploy',    name: 'IoT Device Deployment Package',  category: 'Smart Buildings', description: 'Enterprise IoT deployment plan: sensor placement, gateway locations, network architecture, and integration with BAS/DCIM platforms.',              basePrice: 1499, deliveryHours: 24, tier: 'enterprise',   animationType: 'wifi_design',   badge: 'Demo' },
  { id: 'sb-bas-integration',name: 'BAS Integration Planning',      category: 'Smart Buildings', description: 'Building Automation System integration design: controller mapping, control sequences, point list, and HVAC/lighting/access coordination.',          basePrice: 1199, deliveryHours: 20, tier: 'professional', animationType: 'generic',       badge: 'Demo' },
  { id: 'sb-digital-twin',  name: 'Digital Twin Enablement',        category: 'Smart Buildings', description: 'Connect your building to R2 Digital Twin: live data streams, IoT sensor onboarding, dashboards, and real-time occupancy analytics.',               basePrice: 2999, deliveryHours: 72, tier: 'enterprise',   animationType: 'digital_twin',  badge: 'Demo' },

  // ── Renovation ─────────────────────────────────────────────────────────────
  { id: 'reno-intel-pkg',   name: 'Renovation Intelligence Package', category: 'BIM & Digital Twin', description: 'AI-powered room-by-room renovation survey: upload room photos, get A0–A6 sheet set output (Existing Conditions, Technology Layouts, Wall Coordination, Equipment Schedule) with professional VERIFY IN FIELD markers. Phase 1: template room. Phase 2: batch all rooms.', basePrice: 1899, deliveryHours: 20, tier: 'professional', animationType: 'digital_twin',  badge: 'New' },
];

const TIER_COLORS = { starter: '#4ade80', professional: '#00d4ff', enterprise: '#ff6b35' };

// Precompute which categories actually have services (for SOON badges)
const CATS_WITH_PRODUCTS = new Set(SERVICES.map(s => s.category));

// ─── Animated grid background ─────────────────────────────────────────────────
function GridBackground(): JSX.Element {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `
          linear-gradient(rgba(0,212,255,0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0,212,255,0.03) 1px, transparent 1px)
        `,
        backgroundSize: '44px 44px',
      }}/>
      <div style={{ position: 'absolute', top: '15%', left: '5%', width: 500, height: 500, background: 'radial-gradient(circle, rgba(0,212,255,0.04) 0%, transparent 70%)', borderRadius: '50%' }}/>
      <div style={{ position: 'absolute', bottom: '10%', right: '8%', width: 400, height: 400, background: 'radial-gradient(circle, rgba(255,107,53,0.04) 0%, transparent 70%)', borderRadius: '50%' }}/>
    </div>
  );
}

// ─── Service card ─────────────────────────────────────────────────────────────
function ServiceCard({ service, onPreview, onOrder }: { service: Service; onPreview: () => void; onOrder: () => void }): JSX.Element {
  const access = serviceCommercial(service);
  const [hovered, setHovered] = useState(false);
  const founderMode = useFounderMode(s => s.active) || hasFounderToken();
  const tierColor = TIER_COLORS[service.tier];

  return (
    <div
      data-testid={`service-card-${service.id}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? 'rgba(0,212,255,0.04)' : C.card,
        border: `1px solid ${hovered ? C.borderHov : C.border}`,
        borderRadius: 14,
        padding: '18px 18px 14px',
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 0.2s, box-shadow 0.2s, background 0.2s',
        boxShadow: hovered ? '0 0 32px rgba(0,212,255,0.07), 0 8px 32px rgba(0,0,0,0.4)' : '0 2px 12px rgba(0,0,0,0.3)',
        cursor: 'default',
      }}>

      {/* Blueprint hover overlay */}
      {hovered && (
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 19px, rgba(0,212,255,0.04) 20px), repeating-linear-gradient(90deg, transparent, transparent 19px, rgba(0,212,255,0.04) 20px)`,
          borderRadius: 14,
        }}/>
      )}

      {/* Badges */}
      <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: 4, flexDirection: 'column', alignItems: 'flex-end' }}>
        <span style={{ fontFamily: C.mono, fontSize: 8, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: `${tierColor}15`, border: `1px solid ${tierColor}30`, color: tierColor, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{service.tier}</span>
        {service.badge && (
          <span style={{ fontFamily: C.mono, fontSize: 8, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: 'rgba(255,107,53,0.12)', border: '1px solid rgba(255,107,53,0.3)', color: '#ff6b35', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{service.badge}</span>
        )}
      </div>

      {/* Category */}
      <div style={{ fontFamily: C.mono, fontSize: 9, color: 'rgba(0,212,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 6 }}>{service.category}</div>

      {/* Name */}
      <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 6, paddingRight: 60, lineHeight: 1.2 }}>{service.name}</div>

      {/* Description */}
      <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.55, marginBottom: 14, minHeight: 50 }}>{service.description}</div>

      {/* Value-first package indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'rgba(0,212,255,0.04)', border: '1px dashed rgba(0,212,255,0.2)', borderRadius: 7 }}>
          <Package size={11} style={{ color: 'rgba(0,212,255,0.5)' }}/>
          <span style={{ fontFamily: C.mono, fontSize: 10.5, color: 'rgba(0,212,255,0.55)', letterSpacing: '0.04em' }}>{founderMode ? 'Founder Access' : `${access.priceLabel} · ${access.accessLabel}`}</span>
        </div>
        <span style={{ fontFamily: C.mono, fontSize: 10, color: 'rgba(240,246,255,0.25)', display: 'flex', alignItems: 'center', gap: 3 }}>
          <Clock size={9}/>{service.deliveryHours}h est.
        </span>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, marginTop: 4, paddingTop: 2 }}>
        <button data-testid={`preview-btn-${service.id}`}
          onClick={onPreview}
          style={{
            flex: 1, minWidth: 0, padding: '10px 12px', borderRadius: 8, boxSizing: 'border-box',
            background: 'transparent', border: `1px solid ${C.cyan}`,
            color: C.cyan, cursor: 'pointer', fontSize: 11.5, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,212,255,0.08)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}>
          <Eye size={12}/> View Preview
        </button>
        <button data-testid={`order-btn-${service.id}`}
          onClick={onOrder}
          style={{
            flex: 1.3, minWidth: 0, padding: '10px 12px', borderRadius: 8, boxSizing: 'border-box',
            background: C.orange, border: 'none', color: '#010812',
            cursor: 'pointer', fontSize: 11.5, fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          }}>
          {founderMode ? 'Open Workflow' : 'View Package'} <ChevronRight size={12}/>
        </button>
      </div>
    </div>
  );
}

// ─── Featured spotlight card ──────────────────────────────────────────────────
function FeaturedCard({ service, onPreview, onOrder }: { service: Service; onPreview: () => void; onOrder: () => void }): JSX.Element {
  const access = serviceCommercial(service);
  return (
    <div data-testid={`featured-card-${service.id}`}
      style={{
        background: 'linear-gradient(135deg, rgba(0,212,255,0.06) 0%, rgba(6,15,35,0.98) 60%)',
        border: `1px solid rgba(0,212,255,0.3)`,
        borderRadius: 18, padding: '22px 24px', position: 'relative', overflow: 'hidden',
        boxShadow: '0 0 60px rgba(0,212,255,0.06), 0 8px 32px rgba(0,0,0,0.5)',
      }}>
      <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, background: 'radial-gradient(circle, rgba(0,212,255,0.07) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }}/>
      <div style={{ fontFamily: C.mono, fontSize: 9, color: 'rgba(255,107,53,0.8)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4 }}>FEATURED SERVICE</div>
      <div style={{ fontSize: 20, fontWeight: 900, color: C.text, marginBottom: 6, lineHeight: 1.2 }}>{service.name}</div>
      {service.badge && (
        <span style={{ display: 'inline-block', fontFamily: C.mono, fontSize: 9, fontWeight: 800, padding: '3px 9px', borderRadius: 20, background: 'rgba(255,107,53,0.15)', border: '1px solid rgba(255,107,53,0.4)', color: '#ff6b35', textTransform: 'uppercase', marginBottom: 10 }}>{service.badge}</span>
      )}
      <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.6, marginBottom: 14, maxWidth: 340 }}>{service.description}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '9px 14px', background: 'rgba(0,212,255,0.04)', border: '1px dashed rgba(0,212,255,0.2)', borderRadius: 9, width: 'fit-content' }}>
        <Lock size={13} style={{ color: 'rgba(0,212,255,0.5)' }}/>
        <span style={{ fontFamily: C.mono, fontSize: 11.5, color: 'rgba(0,212,255,0.6)', letterSpacing: '0.04em' }}>{access.priceLabel} access · Preview deliverables first</span>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <button onClick={onPreview}
          style={{ padding: '10px 18px', borderRadius: 9, background: 'transparent', border: `1.5px solid ${C.cyan}`, color: C.cyan, cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, minWidth: 132, justifyContent: 'center' }}>
          <Play size={12} style={{ fill: C.cyan }}/> Watch Preview
        </button>
        <button onClick={onOrder}
          style={{ padding: '10px 22px', borderRadius: 9, background: C.orange, border: 'none', color: '#010812', cursor: 'pointer', fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 6, minWidth: 128, justifyContent: 'center' }}>
          View Package <ChevronRight size={13}/>
        </button>
      </div>
    </div>
  );
}

function RevenueAccessControlCenter(): JSX.Element {
  const [mode, setMode] = useState<R2CommercialMode>(() => evaluateCommercialAccess('marketplace').mode);
  const products = Object.values(R2_PRODUCT_CATALOG);
  const applyMode = (next: R2CommercialMode) => {
    setCommercialMode(next);
    setMode(next);
  };
  return (
    <section data-testid="revenue-access-control-center" style={{ border: '1px solid rgba(53,215,240,0.22)', borderRadius: 16, padding: 14, marginBottom: 18, background: 'linear-gradient(135deg, rgba(53,215,240,0.08), rgba(5,12,24,0.82))' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.cyan, fontFamily: C.mono, fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase' }}><SlidersHorizontal size={13}/> R2 Revenue & Access Control Center</div>
          <div style={{ color: C.muted, fontSize: 12, marginTop: 5 }}>Central product catalog controls pricing, access, beta/free status, checkout, download, and visibility labels globally.</div>
        </div>
        <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
          {([
            ['founder_beta', 'Founder Beta Mode'], ['free_preview', 'Free Preview Mode'], ['production_paid', 'Production Paid Mode'], ['investor_demo', 'Investor Demo Mode'],
          ] as Array<[R2CommercialMode, string]>).map(([id, label]) => (
            <button key={id} data-testid={`commercial-mode-${id}`} onClick={() => applyMode(id)} style={{ border: `1px solid ${mode === id ? C.cyan : 'rgba(255,255,255,0.1)'}`, background: mode === id ? 'rgba(53,215,240,0.14)' : 'rgba(255,255,255,0.03)', color: mode === id ? '#fff' : C.muted, borderRadius: 999, padding: '7px 10px', fontSize: 10.5, fontWeight: 900, cursor: 'pointer' }}>{label}</button>
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 8 }}>
        {products.map(product => {
          const decision = evaluateCommercialAccess(product.id, mode);
          return <div key={product.id} data-testid={`commercial-product-${product.id}`} style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, background: 'rgba(0,0,0,0.18)', padding: 10 }}>
            <div style={{ color: C.text, fontSize: 12, fontWeight: 900 }}>{product.name}</div>
            <div style={{ color: C.cyan, fontFamily: C.mono, fontSize: 10, marginTop: 4 }}>{decision.priceLabel} · {decision.accessLabel}</div>
            <div style={{ color: C.muted, fontSize: 10.5, marginTop: 5, lineHeight: 1.4 }}>{product.accessRequirement} · preview {decision.canPreview ? 'on' : 'off'} · checkout {decision.canCheckout ? 'on' : 'off'}</div>
          </div>;
        })}
      </div>
    </section>
  );
}

// ─── Order flow modal ─────────────────────────────────────────────────────────
function QuickOrderModal({ service, onClose }: { service: Service | null; onClose: () => void }): JSX.Element | null {
  const userEmail = useAuthStore(s => s.user?.email);
  const founderActive = useFounderMode(s => s.active);
  const founderMode = isMasterAdmin(userEmail) || founderActive || hasFounderToken();
  const founderToken = useFounderMode(s => s.token) || getFounderToken();
  const [form, setForm] = useState({ name: '', email: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState('');

  if (!service) return null;

  const submit = async () => {
    if (!form.email.trim() || !form.name.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/r2-marketplace/orders/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(founderMode && founderToken ? { Authorization: `Bearer ${founderToken}` } : {}),
          ...(founderMode ? { 'X-R2-Founder-Bypass': 'true', 'X-Admin-Email': userEmail || form.email } : {}),
        },
        body: JSON.stringify({
          service_id: service.id,
          customer_name: form.name,
          customer_email: form.email,
          founder_bypass: founderMode,
          audit_reason: founderMode ? 'Founder self-serve free access request — no payment prompt' : undefined,
        }),
      }).then(r => r.json());
      if (founderMode) {
        recordFounderCheckoutAudit({
          mode: 'online', service_id: service.id, service_name: service.name,
          customer_email: form.email, order_id: res.order_id, order_number: res.order_number,
          payment_status: res.payment_status || 'founder_bypassed', payment_prompt_shown: false,
        });
      }
      setDone(res.order_number || res.order_id || 'ORD-' + Date.now());
    } catch {
      if (founderMode) {
        const auditId = recordFounderCheckoutAudit({
          mode: 'offline', service_id: service.id, service_name: service.name,
          customer_email: form.email, payment_status: 'founder_bypassed_offline', payment_prompt_shown: false,
        });
        setDone(auditId);
      } else {
        setDone('ORD-' + Date.now().toString().slice(-6));
      }
    }
    setSubmitting(false);
  };

  const fieldStyle = { width: '100%', background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(0,212,255,0.15)', borderRadius: 8, padding: '9px 12px', color: C.text, fontSize: 12.5, boxSizing: 'border-box' as const };

  return (
    <div data-testid="quick-order-modal"
      style={{ position: 'fixed', inset: 0, background: 'rgba(1,8,18,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: '100%', maxWidth: 460, background: '#010e22', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 18, overflow: 'hidden', boxShadow: '0 0 60px rgba(0,212,255,0.08)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(0,212,255,0.1)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <ShoppingCart size={15} style={{ color: C.orange }}/>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>Free Access Request: {service.name}</div>
            <div style={{ fontFamily: C.mono, fontSize: 10, color: C.muted }}>{founderMode ? 'Founder Access · ' : ''}est. {service.deliveryHours}h turnaround</div>
          </div>
          <button data-testid="quick-order-close-btn" onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.muted }}><X size={15}/></button>
        </div>

        <div style={{ padding: 20 }}>
          {done ? (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <CheckCircle2 size={42} style={{ color: C.green, marginBottom: 12 }}/>
              <div data-testid="order-success-title" style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 6 }}>{founderMode ? 'Founder Access Activated' : 'Free Access Request Submitted'}</div>
              <div data-testid="order-success-number" style={{ fontFamily: C.mono, fontSize: 12, color: C.cyan, marginBottom: 10 }}>Request #{done}</div>
              {founderMode && <div data-testid="founder-checkout-audit-badge" style={{ display: 'inline-flex', marginBottom: 10, padding: '4px 9px', borderRadius: 999, background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.32)', color: C.amber, fontFamily: C.mono, fontSize: 9, fontWeight: 800, textTransform: 'uppercase' }}>Founder bypass audited · no payment prompt</div>}
              <div data-testid="order-success-message" style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
                {founderMode ? <>Founder access has activated this request immediately.<br/>The access event is marked internally for audit.</> : <>R2 will use this request to prepare a detailed scope preview.<br/>Commercial package discussions stay separate from this free access step.</>}
              </div>
              <button data-testid="quick-order-done-btn" onClick={onClose} style={{ marginTop: 16, padding: '9px 24px', borderRadius: 8, background: C.orange, border: 'none', color: '#010812', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>Done</button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={{ fontSize: 10.5, fontFamily: C.mono, color: C.muted, display: 'block', marginBottom: 5 }}>FULL NAME *</label>
                  <input data-testid="order-name-input" value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Your name" style={fieldStyle}/>
                </div>
                <div>
                  <label style={{ fontSize: 10.5, fontFamily: C.mono, color: C.muted, display: 'block', marginBottom: 5 }}>EMAIL ADDRESS *</label>
                  <input data-testid="order-email-input" type="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} placeholder="you@company.com" style={fieldStyle}/>
                </div>
                <div>
                  <label style={{ fontSize: 10.5, fontFamily: C.mono, color: C.muted, display: 'block', marginBottom: 5 }}>PROJECT NOTES (optional)</label>
                  <textarea data-testid="order-notes-input" value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} placeholder="Describe your project…" rows={3} style={{ ...fieldStyle, resize: 'none', lineHeight: 1.5 }}/>
                </div>
              </div>
              <div style={{ background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.12)', borderRadius: 8, padding: '9px 12px', marginBottom: 16, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <Shield size={12} style={{ color: C.cyan, flexShrink: 0, marginTop: 2 }}/>
                <div style={{ fontSize: 10.5, color: C.muted, lineHeight: 1.5 }}>Professional engineers review every request. Typical package preview turnaround: <strong style={{ color: C.text }}>{service.deliveryHours}h</strong>. No payment is captured in this flow.</div>
              </div>
              <button data-testid="order-submit-btn"
                onClick={submit} disabled={submitting || !form.name.trim() || !form.email.trim()}
                style={{ width: '100%', padding: '12px 0', borderRadius: 10, background: (submitting || !form.name.trim() || !form.email.trim()) ? 'rgba(255,107,53,0.3)' : C.orange, border: 'none', color: '#010812', fontWeight: 800, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                {submitting ? <><Loader2 size={14} style={{ animation: 'spin 0.8s linear infinite' }}/> Processing…</> : <>{founderMode ? 'Submit Founder Request' : 'Submit Free Access Request'} <ChevronRight size={15}/></>}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

interface R2VendingMachineLandingProps {
  onSignIn?: () => void;
}

export function R2VendingMachineLanding({ onSignIn }: R2VendingMachineLandingProps = {}): JSX.Element {
  useEffect(() => { void useFounderMode.getState().hydrate(); }, []);
  const [activeCategory, setActiveCategory] = useState('All Services');
  const [searchQuery,    setSearchQuery]    = useState('');
  const [previewService,   setPreviewService]   = useState<Service | null>(null);
  const [discoveryService, setDiscoveryService] = useState<Service | null>(null);
  const [checkoutService,  setCheckoutService]  = useState<Service | null>(null);
  const [showWalkthrough,  setShowWalkthrough]  = useState(false);
  const [showDemoBanner, setShowDemoBanner] = useState(() =>
    localStorage.getItem('r2_demo_banner_dismissed') !== '1'
  );

  // ── Category nav bar state ──────────────────────────────────────────────────
  const scrollRef    = useRef<HTMLDivElement>(null);
  const moreRef      = useRef<HTMLDivElement>(null);
  const dragState    = useRef({ active: false, startX: 0, scrollLeft: 0, velX: 0, lastX: 0, lastT: 0 });
  const rafRef       = useRef<number | null>(null);
  const [canLeft,    setCanLeft]    = useState(false);
  const [canRight,   setCanRight]   = useState(true);
  const [showMore,   setShowMore]   = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  const scrollByAmount = useCallback((px: number) => {
    scrollRef.current?.scrollBy({ left: px, behavior: 'smooth' });
  }, []);

  const selectCat = useCallback((cat: string) => {
    setActiveCategory(cat);
    setShowMore(false);
    requestAnimationFrame(() => {
      const el  = scrollRef.current;
      if (!el) return;
      const btn = el.querySelector(`[data-cat="${CSS.escape(cat)}"]`) as HTMLElement | null;
      if (!btn) return;
      const cr = el.getBoundingClientRect();
      const br = btn.getBoundingClientRect();
      el.scrollBy({ left: br.left - cr.left - cr.width / 2 + br.width / 2, behavior: 'smooth' });
    });
  }, []);

  // resize / initial scroll state
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    const ro = new ResizeObserver(checkScroll);
    ro.observe(el);
    return () => ro.disconnect();
  }, [checkScroll]);

  // close "More" on outside click
  useEffect(() => {
    if (!showMore) return;
    const handler = (e: MouseEvent) => {
      if (!moreRef.current?.contains(e.target as Node)) setShowMore(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMore]);

  // ── Drag handlers ───────────────────────────────────────────────────────────
  const onDragStart = useCallback((e: React.MouseEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    dragState.current = { active: true, startX: e.pageX - el.offsetLeft, scrollLeft: el.scrollLeft, velX: 0, lastX: e.pageX, lastT: Date.now() };
    setIsDragging(true);
  }, []);

  const onDragMove = useCallback((e: React.MouseEvent) => {
    if (!dragState.current.active) return;
    e.preventDefault();
    const el = scrollRef.current;
    if (!el) return;
    const now = Date.now(); const dt = now - dragState.current.lastT;
    if (dt > 0) dragState.current.velX = (e.pageX - dragState.current.lastX) / dt;
    dragState.current.lastX = e.pageX; dragState.current.lastT = now;
    el.scrollLeft = dragState.current.scrollLeft - (e.pageX - el.offsetLeft - dragState.current.startX) * 1.1;
    checkScroll();
  }, [checkScroll]);

  const onDragEnd = useCallback(() => {
    if (!dragState.current.active) return;
    dragState.current.active = false;
    setIsDragging(false);
    // Momentum coast
    let v = dragState.current.velX * -18;
    const el = scrollRef.current;
    if (!el || Math.abs(v) < 0.4) return;
    const coast = () => {
      v *= 0.90; el.scrollLeft += v; checkScroll();
      if (Math.abs(v) > 0.3) rafRef.current = requestAnimationFrame(coast);
    };
    rafRef.current = requestAnimationFrame(coast);
  }, [checkScroll]);

  const onWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const el = scrollRef.current;
    if (!el) return;
    el.scrollLeft += e.deltaY + e.deltaX;
    checkScroll();
  }, [checkScroll]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    const idx = ALL_CATS.indexOf(activeCategory);
    if (e.key === 'ArrowRight') { e.preventDefault(); selectCat(ALL_CATS[Math.min(idx + 1, ALL_CATS.length - 1)]!); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); selectCat(ALL_CATS[Math.max(idx - 1, 0)]!); }
    else if (e.key === 'Home') { e.preventDefault(); selectCat(ALL_CATS[0]!); }
    else if (e.key === 'End')  { e.preventDefault(); selectCat(ALL_CATS[ALL_CATS.length - 1]!); }
  }, [activeCategory, selectCat]);

  const filtered = useMemo(() => {
    return SERVICES.filter(s => {
      const matchCat = activeCategory === 'All Services' || s.category === activeCategory;
      const q = searchQuery.toLowerCase();
      const matchSearch = !q || s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q) || s.category.toLowerCase().includes(q);
      return matchCat && matchSearch;
    });
  }, [activeCategory, searchQuery]);

  const featured = SERVICES.filter(s => s.featured).slice(0, 3);

  return (
    <div data-testid="marketplace-landing" style={{ background: C.bg, minHeight: '100vh', color: C.text, position: 'relative' }}>
      <GridBackground/>

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* ── Top Navbar ─────────────────────────────────────────────────── */}
        <header style={{ position: 'sticky', top: 0, zIndex: 200, background: 'rgba(1,8,18,0.92)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(0,212,255,0.1)', padding: '10px 24px' }}>
          <div style={{ maxWidth: 1440, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 20 }}>
            {/* Logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)', display: 'grid', placeItems: 'center' }}>
                <Zap size={16} style={{ color: C.cyan }}/>
              </div>
              <div>
                <div style={{ fontFamily: C.mono, fontSize: 13, fontWeight: 800, color: C.text, letterSpacing: '0.06em' }}>R2 ENGINE</div>
                <div style={{ fontFamily: C.mono, fontSize: 8, color: 'rgba(0,212,255,0.5)', letterSpacing: '0.12em' }}>MARKETPLACE</div>
              </div>
            </div>

            {/* Search */}
            <div style={{ flex: 1, maxWidth: 520, position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'rgba(0,212,255,0.5)' }}/>
              <input
                data-testid="marketplace-search"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search engineering services… (floor plan, data centre, hospital, OSP…)"
                style={{ width: '100%', background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.15)', borderRadius: 24, padding: '8px 14px 8px 36px', color: C.text, fontSize: 12.5, boxSizing: 'border-box', fontFamily: 'inherit' }}
              />
            </div>

            {/* Trust signals */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.muted }}>
                <Shield size={12} style={{ color: C.green }}/> Professional Engineers
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.muted }}>
                <Bot size={12} style={{ color: C.cyan }}/> AI-Powered
              </div>
              <LearnThisFeature courseId="r2-103" label="How It Works" position="inline"/>
              <button
                data-testid="investor-safe-demo-btn"
                type="button"
                onClick={() => { window.location.hash = '#investor-demo'; }}
                style={{ padding: '7px 14px', borderRadius: 8, background: 'rgba(244,201,93,0.10)', border: '1px solid rgba(244,201,93,0.35)', color: C.amber, cursor: 'pointer', fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 5 }}>
                <Sparkles size={12}/> Investor Demo
              </button>
              {/* Secondary Login */}
              <button
                data-testid="marketplace-sign-in"
                type="button"
                onClick={onSignIn}
                style={{ padding: '7px 16px', borderRadius: 8, background: 'transparent', border: '1px solid rgba(240,246,255,0.15)', color: 'rgba(240,246,255,0.5)', cursor: 'pointer', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}>
                <LogIn size={12}/> Sign In
              </button>
            </div>
          </div>
        </header>

        {/* ── Hero Strip ─────────────────────────────────────────────────── */}
        <div style={{ padding: '32px 24px 20px', maxWidth: 1440, margin: '0 auto' }}>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontFamily: C.mono, fontSize: 10, color: 'rgba(0,212,255,0.6)', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 10 }}>
              {SERVICES.length} AI Engineering Services · Preview Deliverables Before Package Options
            </div>
            <h1 style={{ fontSize: 38, fontWeight: 900, color: C.text, margin: 0, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
              What can <span style={{ color: C.cyan }}>R2</span> build<br/>for you?
            </h1>
          </div>

          {/* ── Golden Path Demo Banner ──────────────────────────────────── */}
          {showDemoBanner && (
            <div data-testid="demo-banner"
              style={{ marginBottom: 24, padding: '14px 18px', background: 'linear-gradient(90deg, rgba(255,107,53,0.06) 0%, rgba(0,212,255,0.04) 100%)', border: '1px solid rgba(255,107,53,0.25)', borderRadius: 14, position: 'relative' }}>
              <button onClick={() => { setShowDemoBanner(false); localStorage.setItem('r2_demo_banner_dismissed', '1'); }}
                style={{ position: 'absolute', top: 10, right: 12, background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(240,246,255,0.3)', display: 'flex' }}><X size={14}/></button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <Zap size={14} style={{ color: C.orange }}/>
                <span style={{ fontFamily: C.mono, fontSize: 9, color: 'rgba(255,107,53,0.7)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>Golden Path — Customer Demo</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap', rowGap: 8 }}>
                {[
                  { n: '01', label: 'Select Service' },
                  { n: '02', label: 'Discovery Questions' },
                  { n: '03', label: 'Value Preview' },
                  { n: '04', label: 'What You Receive' },
                  { n: '05', label: 'View Package' },
                  { n: '06', label: 'Free Access' },
                ].map((step, i, arr) => (
                  <div key={step.n} style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(255,107,53,0.12)', border: '1px solid rgba(255,107,53,0.3)', display: 'grid', placeItems: 'center', fontFamily: C.mono, fontSize: 8, color: C.orange, fontWeight: 800, flexShrink: 0 }}>{step.n}</div>
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: 'rgba(240,246,255,0.7)', whiteSpace: 'nowrap' }}>{step.label}</span>
                    </div>
                    {i < arr.length - 1 && <ChevronRight size={12} style={{ color: 'rgba(255,107,53,0.3)', margin: '0 6px' }}/>}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 12 }}>
                <button
                  data-testid="golden-path-start-btn"
                  onClick={() => {
                    setShowDemoBanner(false);
                    localStorage.setItem('r2_demo_banner_dismissed', '1');
                    // Reset returning-user flag so full golden path shows (all 3 steps)
                    localStorage.removeItem('r2_mp_completed_services');
                    const dataCentre = SERVICES.find(s => s.id.includes('data') || s.name.toLowerCase().includes('data'));
                    if (dataCentre) { void trackFunnel('golden_path_started', dataCentre.id); setDiscoveryService(dataCentre); }
                  }}
                  style={{ padding: '8px 18px', borderRadius: 8, background: C.orange, border: 'none', color: '#010812', fontWeight: 800, fontSize: 12, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Zap size={13}/> Start Demo — Data Centre Design <ChevronRight size={13}/>
                </button>
              </div>
            </div>
          )}

          <PreviewExperiencePanel
            testId="marketplace-preview-standard"
            accent={C.cyan}
            title="Browse by output — not by price"
            subtitle="Every R2 capability now follows the same preview-first pattern: what it creates, what the customer receives, example output, interactive preview, and clear benefit before package options."
            creates={['Engineering drawings and packages', 'Digital twin and BIM workspaces', 'Academy learning paths', 'Marketplace service previews']}
            receives={['Example output preview', 'Deliverable list', 'Customer outcome summary', 'Package options only after intent']}
            example={<MiniSheetPreview accent={C.cyan} rows={5}/>} 
            benefit="This reduces friction by showing real value before asking customers to activate, request quote, or contact sales."
            interactiveLabel="Show Me What I Get"
            onInteractive={() => { void trackFunnel('show_me_what_i_get', 'cinematic-walkthrough'); setShowWalkthrough(true); }}
          />

          <section data-testid="packet-journey-front-card" style={{ border: '1px solid rgba(53,215,240,0.22)', borderRadius: 16, padding: 16, marginBottom: 18, background: 'linear-gradient(135deg, rgba(53,215,240,0.10), rgba(5,12,24,0.86))', display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <div style={{ color: C.cyan, fontFamily: C.mono, fontSize: 10, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 5 }}>30-second cinematic video</div>
              <div style={{ color: C.text, fontWeight: 950, fontSize: 20 }}>Data Packet Journey — from device to destination</div>
              <div style={{ color: C.muted, fontSize: 12, marginTop: 5 }}>16:9 first-person photon/electron network journey with silent downloadable WebM export.</div>
            </div>
            <button data-testid="open-data-packet-journey-button" onClick={() => { window.location.hash = 'data-packet-journey'; }} style={{ border: 'none', borderRadius: 10, background: C.cyan, color: '#020617', padding: '12px 15px', fontWeight: 950, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}><Play size={15}/> Open Video</button>
          </section>

          <RevenueAccessControlCenter />

          <R2FounderBetaProgram
            onLaunchWalkthrough={() => setShowWalkthrough(true)}
            onNavigate={(route) => { window.location.hash = route; }}
          />

          {/* Featured grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14, marginBottom: 32 }}>
            {featured.map(s => (
              <FeaturedCard key={s.id} service={s}
                onPreview={() => { void trackFunnel('preview_modal_opened', s.id); setPreviewService(s); }}
                onOrder={() => { void trackFunnel('order_initiated', s.id); setDiscoveryService(s); }}
              />
            ))}
          </div>

          {/* Stats bar */}
          <div style={{ display: 'flex', gap: 24, padding: '14px 20px', background: 'rgba(0,212,255,0.03)', border: '1px solid rgba(0,212,255,0.08)', borderRadius: 10, marginBottom: 24, flexWrap: 'wrap' }}>
            {[
              { val: '28+', label: 'AI Services' },
              { val: '4h', label: 'Typical Turnaround' },
              { val: '100%', label: 'Satisfaction Guarantee' },
              { val: 'P.Eng', label: 'Reviewed by Engineers' },
              { val: 'ISO', label: 'Standards Compliant' },
            ].map(stat => (
              <div key={stat.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: C.mono, fontSize: 14, fontWeight: 900, color: C.cyan }}>{stat.val}</span>
                <span style={{ fontSize: 11, color: C.muted }}>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Premium Category Navigation Bar ─────────────────────────────── */}
        {/* Inject scrollbar-hide CSS once */}
        <style>{`.r2-cat-scroll::-webkit-scrollbar{display:none} .r2-cat-pill:focus-visible{outline:none;box-shadow:0 0 0 2px #36F6FF,0 0 0 4px #0B0F18} .r2-cat-pill:hover:not(.active){transform:translateY(-1px);border-color:rgba(0,212,255,0.3)!important;color:#94a3b8!important;background:rgba(0,212,255,0.05)!important}`}</style>

        <div style={{ position: 'sticky', top: 57, zIndex: 100, background: 'rgba(11,15,24,0.97)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(0,212,255,0.07)' }}>
          <div style={{ maxWidth: 1440, margin: '0 auto', position: 'relative', height: 48, display: 'flex', alignItems: 'center' }}>

            {/* Left edge gradient */}
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 72, background: 'linear-gradient(to right, #0B0F18 30%, transparent)', pointerEvents: 'none', zIndex: 4, opacity: canLeft ? 1 : 0, transition: 'opacity 0.25s' }} />

            {/* Right edge gradient */}
            <div style={{ position: 'absolute', right: 108, top: 0, bottom: 0, width: 72, background: 'linear-gradient(to left, #0B0F18 30%, transparent)', pointerEvents: 'none', zIndex: 4, opacity: canRight ? 1 : 0, transition: 'opacity 0.25s' }} />

            {/* Left chevron */}
            <button
              aria-label="Scroll categories left"
              onClick={() => scrollByAmount(-240)}
              style={{ position: 'absolute', left: 10, zIndex: 5, width: 28, height: 28, borderRadius: '50%', border: '1px solid rgba(0,212,255,0.2)', background: 'rgba(0,212,255,0.08)', color: '#36F6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: canLeft ? 1 : 0, pointerEvents: canLeft ? 'auto' : 'none', transition: 'opacity 0.25s, transform 0.15s', flexShrink: 0 }}
              onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
            >
              <ChevronLeft size={13} />
            </button>

            {/* Scrollable pill container */}
            <div
              ref={scrollRef}
              role="tablist"
              aria-label="Marketplace Categories"
              className="r2-cat-scroll"
              onScroll={checkScroll}
              onMouseDown={onDragStart}
              onMouseMove={onDragMove}
              onMouseUp={onDragEnd}
              onMouseLeave={onDragEnd}
              onWheel={onWheel}
              onKeyDown={onKeyDown}
              tabIndex={0}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', gap: 6,
                overflowX: 'auto', scrollbarWidth: 'none',
                padding: '0 48px', height: '100%', outline: 'none',
                cursor: isDragging ? 'grabbing' : 'grab',
                userSelect: 'none',
              }}
            >
              {ALL_CATS.map((cat) => {
                const isActive = activeCategory === cat;
                return (
                  <button
                    key={cat}
                    role="tab"
                    aria-selected={isActive}
                    data-cat={cat}
                    data-testid={`category-${cat.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                    className={`r2-cat-pill${isActive ? ' active' : ''}`}
                    onClick={() => selectCat(cat)}
                    style={{
                      padding: '5px 14px', borderRadius: 20, flexShrink: 0,
                      border: `1px solid ${isActive ? '#36F6FF' : 'rgba(0,212,255,0.12)'}`,
                      background: isActive ? 'rgba(0,255,255,0.12)' : 'transparent',
                      color: isActive ? '#36F6FF' : 'rgba(148,172,210,0.7)',
                      cursor: 'pointer', fontSize: 11.5, fontWeight: isActive ? 700 : 500, whiteSpace: 'nowrap',
                      fontFamily: isActive ? C.mono : 'inherit',
                      boxShadow: isActive ? '0 0 20px rgba(0,255,255,0.4), inset 0 0 10px rgba(0,255,255,0.04)' : 'none',
                      transform: isActive ? 'scale(1.02)' : 'scale(1)',
                      transition: 'all 0.2s ease-out',
                      outline: 'none',
                    }}
                  >
                    {cat}
                    {!CATS_WITH_PRODUCTS.has(cat) && cat !== 'All Services'
                      ? <span style={{ marginLeft: 5, fontSize: 7, fontFamily: C.mono, letterSpacing: '0.06em', padding: '1px 4px', borderRadius: 3, background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.25)', color: '#fbbf24' }}>SOON</span>
                      : isActive && <span style={{ marginLeft: 5, fontFamily: C.mono, fontSize: 9, opacity: 0.65 }}>{filtered.length}</span>
                    }
                  </button>
                );
              })}
            </div>

            {/* Right chevron */}
            <button
              aria-label="Scroll categories right"
              onClick={() => scrollByAmount(240)}
              style={{ position: 'absolute', right: 116, zIndex: 5, width: 28, height: 28, borderRadius: '50%', border: '1px solid rgba(0,212,255,0.2)', background: 'rgba(0,212,255,0.08)', color: '#36F6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: canRight ? 1 : 0, pointerEvents: canRight ? 'auto' : 'none', transition: 'opacity 0.25s, transform 0.15s', flexShrink: 0 }}
              onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
            >
              <ChevronRight size={13} />
            </button>

            {/* ▼ More dropdown */}
            <div ref={moreRef} style={{ position: 'relative', padding: '0 12px 0 4px', flexShrink: 0 }}>
              <button
                data-testid="category-more-btn"
                onClick={() => setShowMore(v => !v)}
                aria-haspopup="listbox"
                aria-expanded={showMore}
                aria-label="More categories"
                style={{
                  padding: '5px 11px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 5,
                  border: `1px solid ${showMore || MORE_CATS.includes(activeCategory) ? 'rgba(0,212,255,0.45)' : 'rgba(0,212,255,0.18)'}`,
                  background: showMore || MORE_CATS.includes(activeCategory) ? 'rgba(0,212,255,0.12)' : 'rgba(0,212,255,0.04)',
                  color: MORE_CATS.includes(activeCategory) ? '#36F6FF' : 'rgba(148,172,210,0.8)',
                  cursor: 'pointer', fontSize: 11.5, fontWeight: 500, whiteSpace: 'nowrap',
                  transition: 'all 0.2s', outline: 'none',
                }}
              >
                <ChevronDown size={11} style={{ transition: 'transform 0.2s', transform: showMore ? 'rotate(180deg)' : 'none' }} />
                More
                {MORE_CATS.includes(activeCategory) && <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#36F6FF', marginLeft: 2 }} />}
              </button>

              {showMore && (
                <div
                  role="listbox"
                  aria-label="More categories"
                  style={{
                    position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                    width: 220, maxHeight: 380, overflowY: 'auto',
                    background: 'rgba(5,9,18,0.98)', border: '1px solid rgba(0,212,255,0.18)', borderRadius: 12,
                    backdropFilter: 'blur(20px)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.85), 0 0 0 1px rgba(0,212,255,0.04)',
                    zIndex: 300, padding: '6px 6px',
                    scrollbarWidth: 'thin', scrollbarColor: 'rgba(0,212,255,0.15) transparent',
                  }}
                >
                  <div style={{ fontSize: 10, color: 'rgba(0,212,255,0.35)', letterSpacing: '0.08em', textTransform: 'uppercase', padding: '4px 10px 6px', fontFamily: C.mono }}>All Categories</div>
                  {MORE_CATS.map(cat => {
                    const isActive = activeCategory === cat;
                    return (
                      <button
                        key={cat}
                        role="option"
                        aria-selected={isActive}
                        data-testid={`more-category-${cat.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                        onClick={() => selectCat(cat)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          width: '100%', padding: '7px 10px', borderRadius: 7, border: 'none',
                          background: isActive ? 'rgba(0,212,255,0.1)' : 'transparent',
                          color: isActive ? '#36F6FF' : 'rgba(148,172,210,0.75)',
                          cursor: 'pointer', fontSize: 12, textAlign: 'left',
                          transition: 'background 0.12s, color 0.12s',
                        }}
                        onMouseEnter={e => { if (!isActive) { (e.currentTarget as HTMLElement).style.background = 'rgba(0,212,255,0.06)'; (e.currentTarget as HTMLElement).style.color = '#94a3b8'; } }}
                        onMouseLeave={e => { if (!isActive) { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'rgba(148,172,210,0.75)'; } }}
                      >
                        {cat}
                        {isActive
                          ? <CheckCircle2 size={12} style={{ color: '#36F6FF', flexShrink: 0 }} />
                          : !CATS_WITH_PRODUCTS.has(cat) && <span style={{ fontSize: 9, fontFamily: C.mono, padding: '1px 5px', borderRadius: 3, background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.22)', color: '#fbbf24', whiteSpace: 'nowrap', flexShrink: 0 }}>Soon</span>
                        }
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        </div>

        {/* ── Service Grid ────────────────────────────────────────────────── */}
        <div style={{ padding: '20px 24px 48px', maxWidth: 1440, margin: '0 auto' }}>
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: C.muted }}>
              {!searchQuery && !CATS_WITH_PRODUCTS.has(activeCategory) ? (
                <>
                  <div style={{ fontSize: 28, marginBottom: 12, opacity: 0.2 }}>🔜</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 8 }}>{activeCategory} — Coming Soon</div>
                  <div style={{ fontSize: 12, maxWidth: 340, margin: '0 auto 20px', lineHeight: 1.65, color: C.muted }}>Our engineering team is building premium {activeCategory} services. Browse all available services in the meantime.</div>
                  <button
                    onClick={() => setActiveCategory('All Services')}
                    style={{ padding: '8px 20px', borderRadius: 8, background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.25)', color: '#00d4ff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                  >
                    Browse All Services
                  </button>
                </>
              ) : (
                <>
                  <Search size={32} style={{ marginBottom: 12, opacity: 0.2 }}/>
                  <div style={{ fontSize: 14 }}>No services found for &ldquo;{searchQuery}&rdquo;</div>
                </>
              )}
            </div>
          ) : (
            <>
              <div style={{ fontFamily: C.mono, fontSize: 10, color: 'rgba(0,212,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14 }}>
                {filtered.length} services · {activeCategory}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                {filtered.map(s => (
                  <ServiceCard key={s.id} service={s}
                    onPreview={() => { void trackFunnel('preview_modal_opened', s.id); setPreviewService(s); }}
                    onOrder={() => { void trackFunnel('order_initiated', s.id); setDiscoveryService(s); }}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* ── Footer Trust Bar ────────────────────────────────────────────── */}
        <div style={{ borderTop: '1px solid rgba(0,212,255,0.08)', padding: '20px 24px', background: 'rgba(0,0,0,0.3)' }}>
          <div style={{ maxWidth: 1440, margin: '0 auto', display: 'flex', gap: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
            {[
              { icon: <Shield size={14}/>, text: 'Professional Engineer Reviewed' },
              { icon: <Clock size={14}/>, text: 'Typical turnaround from 4 hours' },
              { icon: <CheckCircle2 size={14}/>, text: 'Satisfaction Guarantee' },
              { icon: <Bot size={14}/>, text: 'AI-Powered Engineering Intelligence' },
              { icon: <Layers size={14}/>, text: 'BICSI · ANSI/TIA · NEC/CEC Standards' },
            ].map(item => (
              <div key={item.text} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: C.muted }}>
                <span style={{ color: 'rgba(0,212,255,0.5)' }}>{item.icon}</span>{item.text}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Preview Modal ──────────────────────────────────────────────────── */}
      {showWalkthrough && (
        <R2ShowMeWalkthrough
          onClose={() => setShowWalkthrough(false)}
          onNavigate={(route) => { window.location.hash = route; }}
        />
      )}

      {/* ── Preview Modal ──────────────────────────────────────────────────── */}
      {previewService && (
        <R2PreviewModal
          serviceType={previewService.animationType}
          serviceName={previewService.name}
          price={previewService.basePrice}
          delivery={previewService.deliveryHours}
          onClose={() => setPreviewService(null)}
          onOrder={() => { setPreviewService(null); setDiscoveryService(previewService); }}
        />
      )}

      {/* ── Discovery Gate Modal ──────────────────────────────────────────── */}
      {discoveryService && (
        <R2ServiceDiscoveryModal
          service={discoveryService}
          onClose={() => setDiscoveryService(null)}
          onProceedToCheckout={(_answers) => {
            void trackFunnel('order_started', discoveryService.id);
            setCheckoutService(discoveryService);
            setDiscoveryService(null);
          }}
        />
      )}

      {/* ── Quick Order Modal (after discovery completes) ─────────────────── */}
      {checkoutService && (
        <QuickOrderModal service={checkoutService} onClose={() => setCheckoutService(null)}/>
      )}

      <style>{`
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        * { scrollbar-width: thin; scrollbar-color: rgba(0,212,255,0.2) transparent; }
      `}</style>
    </div>
  );
}

export default R2VendingMachineLanding;
